import React, { act, useState } from "react";
import { IoMdEye } from "react-icons/io";
import { MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link, useParams } from "react-router-dom";
import { getHotelById } from "../../../../api/owner/hotels";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getActivitiesByHotelId, addActivity, updateActivity, } from "../../../../api/owner/hotels/activities";
import { updateHotel } from "../../../../api/owner/hotels";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import AddHotelModel from "../../../../components/ui/AddHotelModel";
import Pagination from "../../../../components/common/Pagination";
import { createSlot, deleteSlot, getSlots, updateSlot } from "../../../../api/owner/restaurants/slots";
import { useTranslation } from "../../../../hooks/useTranslation";


const PopupModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
            <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
                <button
                    onClick={onClose}
                    className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
                >
                    ✕
                </button>
                {children}
            </div>
        </div>
    );
};

const UserHotelDetail = () => {
    const { t } = useTranslation();
    const translations = t("slotDetails");
    const [enabled, setEnabled] = useState(true);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const queryClient = useQueryClient();
    const { restaurantId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [isViewing, setIsViewing] = useState(false);
    const [timeSlots, setTimeSlots] = useState([
        { startTime: '', endTime: '', errors: {} },
    ]);
    const [currentSlotIndex, setCurrentSlotIndex] = useState(null);
    const [slotId, setSlotId] = useState("");

    const { data, isLoading } = useQuery({
        queryKey: ["slots", restaurantId],
        queryFn: () => getSlots(restaurantId),
        enabled: !!restaurantId,
    });

    const slots = data?.data?.slots || []

    const { mutate: addSlots , isPending : isAdding } = useMutation({
        mutationFn: createSlot,
        onSuccess: () => {
            queryClient.invalidateQueries(["slots"]);
            toast.success(translations?.messages?.addSuccess || "Slots added successfully");
            setTimeSlots([{ startTime: '', endTime: '', errors: {} }]);
            setSlotId("");
            setCurrentSlotIndex(null);
            setIsEditing(false);
            setIsViewing(false);
            setIsPopupOpen(false);
        },
        onError: (error) => {
            const errorMessage =
                error?.response?.data?.message ||
                (translations?.messages?.addError || "Failed to add hotel. Please try again.");
            toast.error(errorMessage);
        },
    });

    const updateSlotMutation = useMutation({
        mutationFn: (data) => updateSlot(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["slots", restaurantId]);
            setTimeSlots([{ startTime: '', endTime: '', errors: {} }]);
            setSlotId("");
            setCurrentSlotIndex(null);
            setIsEditing(false);
            setIsViewing(false);
            setIsPopupOpen(false);
            toast.success(translations?.messages?.updateSuccess || "Slots updated successfully!");
        },
        onError: (error) => {
            const errorMessage =
                error?.response?.data?.message || (translations?.messages?.updateError || "Failed to update activity. Please try again.");
            toast.error(errorMessage);
        },
    });

    const deleteSlotMutation = useMutation({
        mutationFn: (slotId) => deleteSlot(slotId),
        onSuccess: () => {
            queryClient.invalidateQueries(["slots", restaurantId]);
        },
    });

    const handleInputChange = (index, field, value) => {
        const newSlots = [...timeSlots];
        newSlots[index][field] = value;

        if (newSlots[index].errors[field]) {
            newSlots[index].errors[field] = '';
        }

        setTimeSlots(newSlots);
    };

    const addTimeSlot = () => {
        setTimeSlots([...timeSlots, { startTime: '', endTime: '', errors: {} }]);
    };

    const removeTimeSlot = (index) => {
        const newSlots = timeSlots.filter((_, i) => i !== index);
        setTimeSlots(newSlots);
    };

    const validateSlots = () => {
        let isValid = true;
        const updatedSlots = timeSlots.map((slot) => {
            const errors = {};
            if (!slot.startTime) {
                errors.startTime = translations?.errors?.startTimeRequired || 'Start time is required';
                isValid = false;
            }
            if (!slot.endTime) {
                errors.endTime = translations?.errors?.endTimeRequired || 'End time is required';
                isValid = false;
            } else if (slot.startTime && slot.endTime <= slot.startTime) {
                errors.endTime = translations?.errors?.endTimeAfterStart || 'End time must be after start time';
                isValid = false;
            }
            return { ...slot, errors };
        });

        setTimeSlots(updatedSlots);
        return isValid;
    };


    const closeModal = () => {
        setIsPopupOpen(false);
        setIsEditing(false);
        setIsViewing(false);
        setTimeSlots([{ startTime: '', endTime: '', errors: {} }]);
        setSlotId("");
        setCurrentSlotIndex(null);
    };



    const handleDeleteSlot = (activityId) => {
        const deleteConfirm = translations?.deleteConfirm || {};
        Swal.fire({
            title: deleteConfirm.title || "Are you sure?",
            text: deleteConfirm.text || "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: deleteConfirm.confirm || "Yes, delete it!",
            cancelButtonText: deleteConfirm.cancel || t("common")?.cancel || "Cancel",
        }).then((result) => {
            if (result.isConfirmed) {
                deleteSlotMutation.mutate(activityId, {
                    onSuccess: () => {
                        toast.success(translations?.messages?.deleteSuccess || "Slot deleted successfully!");
                    },
                    onError: (error) => {
                        const errorMessage =
                            error?.response?.data?.message || (translations?.messages?.deleteError || "Failed to delete slot. Please try again.");
                        toast.error(errorMessage);
                    },
                });
            }
        });
    };


    const openModel = () => {
        setTimeSlots([{ startTime: '', endTime: '', errors: {} }]);
        setIsEditing(false);
        setIsViewing(false);
        setSlotId("");
        setCurrentSlotIndex(null);
        setIsPopupOpen(true);
    }

    const slotSubmit = () => {
        const slotPayload =
        {
            restaurantId: restaurantId,
            slots: timeSlots
        }
        addSlots(slotPayload);
    }


    const handleEdit = (slot, index) => {
        if (!slot) return;
        setTimeSlots([{ startTime: slot?.startTime || '', endTime: slot?.endTime || '', errors: {} }]);
        setSlotId(slot?._id);
        setCurrentSlotIndex(0);
        setIsEditing(true);
        setIsViewing(false);
        setIsPopupOpen(true);
    };

    const handleUpdateSlot = async () => {
        const slotPayload =
        {
            restaurantId: restaurantId,
            startTime: timeSlots[0]?.startTime,
            endTime: timeSlots[0]?.endTime
        }

        updateSlotMutation.mutate({ slotId, payload: slotPayload })
    }


    return (
        <>
            <div>
                <h2 className="md:text-[28px] text-[22px] font-semibold text-black">
                    {translations?.title || "Slot Details"}
                </h2>
                <div className="md:mt-[30px] mt-[20px] grid grid-cols-1 ">

                    <div className="border border-[#DDDDDD] py-[30px] px-[25px] rounded-[10px]  bg-white">
                        <div className="flex justify-between gap-[10px] items-center flex-wrap sm:mb-0 mb-[20px]">
                            <h2 className="text-[22px] font-medium text-black">
                                {translations?.availableSlots || "Available Slots"}
                            </h2>
                            <button
                                onClick={openModel}
                                className="inline-block w-full sm:max-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
                            >
                                {translations?.addNew || "+ Add New"}
                            </button>
                        </div>

                        <div className="  overflow-hidden mt-[25px]">
                            <div className="overflow-x-auto ">
                                <table className="min-w-full bg-white ">
                                    <thead className="bg-[#FFFAF2] ">
                                        <tr className="">
                                            <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                                                {translations?.tableHeaders?.seriesNo || "Series No."}
                                            </th>
                                            <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                                                {translations?.tableHeaders?.startTime || "Start Time"}
                                            </th>
                                            <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                                                {translations?.tableHeaders?.endTime || "End Time"}
                                            </th>

                                            <th className="text-left text-lg font-medium text-[#000000] py-[22px] px-[20px] whitespace-nowrap">
                                                {translations?.tableHeaders?.action || "Action"}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>


                                        {
                                            slots?.length > 0 ? (
                                                slots.filter(slot => slot != null).map((slot, index) => (
                                                    <tr key={slot?._id || index} className="border-t border-[#E4E4E4] ">
                                                        <td className="py-[26px] px-[20px] text-base font-normal text-black">
                                                            {index + 1}
                                                        </td>
                                                        <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                                                            {slot?.startTime || '-'}
                                                        </td>
                                                        <td className="py-[16px] px-[32px] text-base font-normal text-black  whitespace-nowrap">
                                                            {slot?.endTime || '-'}
                                                        </td>

                                                        <td className="py-[16px] px-[32px] flex gap-[12px]">

                                                            <button
                                                                onClick={() => handleEdit(slot, index)}
                                                                className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                                                            >
                                                                <MdOutlineEdit className="text-base text-black" />
                                                            </button>
                                                            <button

                                                                onClick={() => {
                                                                    handleDeleteSlot(slot._id);
                                                                }}
                                                                className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                                                            >
                                                                <RiDeleteBin6Line className="text-base text-black" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr className="border-t border-[#E4E4E4] ">
                                                    <td colSpan="6" className="py-[26px] px-[20px] text-base font-normal text-black text-center">
                                                        {translations?.noSlotsAvailable || "No Slots available"}
                                                    </td>
                                                </tr>
                                            )
                                        }

                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            {/* Modal Popup */}
            <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
                <div className="w-full rounded-[20px] p-[30px] mt-5">
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <p className="font-medium">{translations?.modal?.title || "Slots"}</p>

                            {
                                !isEditing && <p
                                    className="cursor-pointer text-xl font-bold"
                                    onClick={addTimeSlot}
                                >
                                    +
                                </p>
                            }

                        </div>

                        {timeSlots.map((slot, index) => (
                            <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-[16px] mb-[19px]">
                                <div className="w-full">
                                    <label className="text-sm text-[#000000] mb-[8px] block">{translations?.modal?.startTime || "Start Time *"}</label>
                                    <input
                                        type="time"
                                        disabled={isViewing}
                                        value={slot.startTime}
                                        onChange={(e) => handleInputChange(index, 'startTime', e.target.value)}
                                        className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${slot.errors.startTime ? 'border-red-500' : 'border-[#DDDDDD]'
                                            }`}
                                    />
                                    {slot.errors.startTime && (
                                        <p className="text-red-500 text-sm mt-1">{slot.errors.startTime}</p>
                                    )}
                                </div>

                                <div className="w-full">
                                    <label className="text-sm text-[#000000] mb-[8px] block">{translations?.modal?.endTime || "End Time *"}</label>
                                    <input
                                        type="time"
                                        disabled={isViewing}
                                        value={slot.endTime}
                                        onChange={(e) => handleInputChange(index, 'endTime', e.target.value)}
                                        className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${slot.errors.endTime ? 'border-red-500' : 'border-[#DDDDDD]'
                                            }`}
                                    />
                                    {slot.errors.endTime && (
                                        <p className="text-red-500 text-sm mt-1">{slot.errors.endTime}</p>
                                    )}
                                </div>

                                <div className="flex justify-end mt-2">
                                    {timeSlots.length > 1 && !isViewing && (
                                        <button
                                            type="button"
                                            onClick={() => removeTimeSlot(index)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            {translations?.modal?.remove || "Remove"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}


                    </div>


                    {
                        !isViewing && <div className="flex gap-[30px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
                            <Link
                                to="#"
                                onClick={closeModal}
                                className="text-white font-bold inline-block py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
                            >
                                {translations?.modal?.cancel || "Cancel"}
                            </Link>
                            <button
                                to="#"
                                onClick={() => {
                                    if (validateSlots()) {
                                        if (isEditing) {
                                            handleUpdateSlot()
                                        } else {
                                            slotSubmit()
                                        }
                                    }
                                }}
                                className="inline-block rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
                            >
                                {isEditing ? (translations?.modal?.update || 'Update') : (translations?.modal?.add || 'Add')}
                            </button>
                        </div>
                    }

                </div>
            </PopupModal>
        </>
    );
};

export default UserHotelDetail;
